import { CanceledError } from "axios";
import CaptchaException from "@rmp/core/exceptions/captchaException";
import type { SolveResult } from "cap-widget";
import type { RequestParams } from "@rmp/api-gateway";

const captchaApiUrl = process.env.VUE_APP_CAPTCHA_API_URL?.trim().replace(/\/+$/, "") ?? "";
const captchaSiteKey = process.env.VUE_APP_CAPTCHA_SITE_KEY?.trim() ?? "";

// Временно отключено вместе с проверкой на бэкенде. Для возврата включить этот флаг.
export const captchaEnabled = false;

export const captchaApiEndpoint = `${captchaApiUrl}/${captchaSiteKey}/`;

export const getCaptchaRequestParams = (token: string) => {
	if(!captchaEnabled) return {};

	if(!token) throw new Error("Пройдите проверку CAPTCHA.");

	return {
		headers: { "cap-token": token },
		// Одноразовый токен нельзя повторно отправлять после обновления сессии.
		skipAuthRefresh: true
	};
};

const throwIfAborted = (signal: AbortSignal) => {
	if(signal.aborted) throw new CanceledError();
};

export const solveCaptcha = async (signal: AbortSignal): Promise<string> => {
	throwIfAborted(signal);

	let captcha: import("cap-widget").default | undefined;
	let onAbort: () => void = () => {};

	try {
		const { default: Cap } = await import("cap-widget");
		throwIfAborted(signal);
		const widget = new Cap({ apiEndpoint: captchaApiEndpoint });
		captcha = widget;
		const result = await new Promise<SolveResult>((resolve, reject) => {
			onAbort = () => reject(new CanceledError());
			signal.addEventListener("abort", onAbort, { once: true });
			widget.solve().then(resolve, reject);
		});

		throwIfAborted(signal);
		if(!result?.success || !result.token) throw new Error("CAPTCHA не вернула токен.");

		return result.token;
	} catch (error) {
		throwIfAborted(signal);
		throw new CaptchaException();
	} finally {
		signal.removeEventListener("abort", onAbort);
		// Удаление виджета отменяет его запросы, останавливает воркеры и очищает таймеры.
		captcha?.widget.remove();
	}
};

export const withCaptcha = <TPayload, TResult>(
	request: (payload: TPayload, params: RequestParams) => Promise<TResult>,
	getSignal: () => AbortSignal
) => async (payload: TPayload): Promise<TResult> => {
	const signal = getSignal();
	const token = captchaEnabled ? await solveCaptcha(signal) : "";
	// Не отправляем запрос после закрытия или повторной инициализации страницы.
	throwIfAborted(signal);
	return request(payload, getCaptchaRequestParams(token));
};
